import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import {
  Loader2, Bot, Send, Hash, Volume2, Folder, Users,
  Wifi, WifiOff, CheckCircle2, XCircle, ChevronDown, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { DiscordTemplateComposer } from '@/components/jartinet/admin/DiscordTemplateComposer';
import { FileText, Sparkles } from 'lucide-react';

interface DiscordGuildSummary {
  id: string;
  name: string;
  icon?: string;
}

interface DiscordChannel {
  id: string;
  name: string;
  type: number;
  parent_id?: string;
  position: number;
}

interface DiscordRole {
  id: string;
  name: string;
  color: number;
  position: number;
}

interface DiscordGuildFull {
  id: string;
  name: string;
  icon?: string;
  approximate_member_count?: number;
}

interface GuildInfo {
  guild: DiscordGuildFull;
  channels: DiscordChannel[];
  roles: DiscordRole[];
}

interface ActionResult {
  action: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error: string | null;
}

interface HistoryEntry {
  id: string;
  type: 'user' | 'bot';
  content: string;
  results?: ActionResult[];
  model?: string;
  timestamp: Date;
  ok?: boolean;
}

const discordGetMyGuilds = httpsCallable(functions, 'discord_getMyGuilds');
const discordGetGuildInfo = httpsCallable(functions, 'discord_getGuildInfo');
const discordAiPrompt = httpsCallable(functions, 'discord_aiPrompt');

const ACTION_LABELS: Record<string, string> = {
  send_message: 'Mensaje enviado',
  edit_channel: 'Canal editado',
  edit_category: 'Categoría editada',
  create_channel: 'Canal creado',
  delete_channel: 'Canal eliminado',
  send_dm: 'DM enviado',
};

function guildIconUrl(guild: DiscordGuildSummary | DiscordGuildFull, size = 64) {
  if (!guild.icon) return null;
  return `https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png?size=${size}`;
}

function GuildAvatar({ guild, size = 'md' }: { guild: DiscordGuildSummary | DiscordGuildFull; size?: 'sm' | 'md' | 'lg' }) {
  const url = guildIconUrl(guild);
  const cls = size === 'sm' ? 'h-8 w-8' : size === 'lg' ? 'h-14 w-14' : 'h-10 w-10';
  if (url) return <img src={url} alt={guild.name} className={cn(cls, 'rounded-xl object-cover flex-shrink-0')} />;
  return (
    <div className={cn(cls, 'rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0')}>
      <Users className={cn('text-primary', size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-7 w-7' : 'h-5 w-5')} />
    </div>
  );
}

function ChannelIcon({ type, className }: { type: number; className?: string }) {
  if (type === 4) return <Folder className={cn('h-3.5 w-3.5', className)} />;
  if (type === 2 || type === 13) return <Volume2 className={cn('h-3.5 w-3.5', className)} />;
  return <Hash className={cn('h-3.5 w-3.5', className)} />;
}

function ChannelTree({ channels }: { channels: DiscordChannel[] }) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const categories = channels.filter(c => c.type === 4).sort((a, b) => a.position - b.position);
  const uncategorized = channels.filter(c => c.type !== 4 && !c.parent_id).sort((a, b) => a.position - b.position);
  const byCategory = (catId: string) => channels.filter(c => c.parent_id === catId).sort((a, b) => a.position - b.position);

  const toggle = (id: string) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const renderChannel = (ch: DiscordChannel, indent = false) => (
    <div
      key={ch.id}
      className={cn(
        'flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 cursor-default transition-colors',
        indent && 'ml-3'
      )}
    >
      <ChannelIcon type={ch.type} className="flex-shrink-0" />
      <span className="truncate">{ch.name}</span>
    </div>
  );

  return (
    <div className="space-y-0.5">
      {uncategorized.map(ch => renderChannel(ch))}
      {categories.map(cat => (
        <div key={cat.id}>
          <button
            onClick={() => toggle(cat.id)}
            className="flex items-center gap-1 px-1 py-1 w-full text-left rounded-lg hover:bg-muted/20 transition-colors"
          >
            {collapsed.has(cat.id)
              ? <ChevronRight className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />
              : <ChevronDown className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />
            }
            <span className="text-[0.62rem] font-bold uppercase tracking-widest text-muted-foreground/60 truncate">
              {cat.name}
            </span>
          </button>
          {!collapsed.has(cat.id) && (
            <div className="space-y-0.5">
              {byCategory(cat.id).map(ch => renderChannel(ch, true))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Guild picker screen ──────────────────────────────────────────
function GuildPicker({
  guilds,
  onSelect,
}: {
  guilds: DiscordGuildSummary[];
  onSelect: (guild: DiscordGuildSummary) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="rounded-xl border border-primary/25 bg-primary/10 p-2">
          <Bot className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-headline text-2xl font-bold text-foreground">Discord Bot</h1>
          <p className="text-sm text-muted-foreground">Selecciona el servidor que quieres controlar</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {guilds.map(guild => (
          <button
            key={guild.id}
            onClick={() => onSelect(guild)}
            className="group flex flex-col items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-4 hover:bg-card/70 hover:border-primary/30 transition-all duration-200"
          >
            <GuildAvatar guild={guild} size="lg" />
            <span className="text-sm font-medium text-foreground text-center leading-tight line-clamp-2 group-hover:text-primary transition-colors">
              {guild.name}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main console ─────────────────────────────────────────────────
export default function AdminDiscordPage() {
  const { userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [guilds, setGuilds] = useState<DiscordGuildSummary[]>([]);
  const [loadingGuilds, setLoadingGuilds] = useState(true);
  const [selectedGuild, setSelectedGuild] = useState<DiscordGuildSummary | null>(null);
  const [guildInfo, setGuildInfo] = useState<GuildInfo | null>(null);
  const [loadingGuildInfo, setLoadingGuildInfo] = useState(false);

  const [prompt, setPrompt] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [mode, setMode] = useState<'template' | 'ai'>('template');

  // Guard
  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [loading, userData, navigate]);

  // Load guilds list
  useEffect(() => {
    if (!userData || userData.role !== 'admin') return;
    setLoadingGuilds(true);
    discordGetMyGuilds({})
      .then(res => {
        const data = res.data as { guilds: DiscordGuildSummary[] };
        setGuilds(data.guilds);
      })
      .catch(err => toast({ variant: 'destructive', title: 'Error cargando servidores', description: err.message }))
      .finally(() => setLoadingGuilds(false));
  }, [userData, toast]);

  // Load guild detail when a guild is selected
  const handleSelectGuild = useCallback((guild: DiscordGuildSummary) => {
    setSelectedGuild(guild);
    setGuildInfo(null);
    setHistory([]);
    setLoadingGuildInfo(true);
    discordGetGuildInfo({ guildId: guild.id })
      .then(res => setGuildInfo(res.data as GuildInfo))
      .catch(err => toast({ variant: 'destructive', title: 'Error cargando servidor', description: err.message }))
      .finally(() => setLoadingGuildInfo(false));
  }, [toast]);

  // Auto-scroll history
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const handleSend = useCallback(async () => {
    const trimmed = prompt.trim();
    if (!trimmed || isExecuting || !guildInfo || !selectedGuild) return;

    setHistory(prev => [...prev, {
      id: Date.now().toString(),
      type: 'user',
      content: trimmed,
      timestamp: new Date(),
    }]);
    setPrompt('');
    setIsExecuting(true);

    try {
      const channelsForAI = guildInfo.channels.map(c => ({ id: c.id, name: c.name, type: c.type, parent_id: c.parent_id }));
      const rolesForAI = guildInfo.roles.map(r => ({ id: r.id, name: r.name }));
      const res = await discordAiPrompt({
        prompt: trimmed,
        channels: channelsForAI,
        roles: rolesForAI,
        guildId: selectedGuild.id,
      });
      const data = res.data as { success: boolean; explanation: string; actions: Record<string, unknown>[]; results: ActionResult[]; model: string };

      const allOk = data.results.every(r => r.error === null);
      setHistory(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: data.explanation,
        results: data.results,
        model: data.model,
        timestamp: new Date(),
        ok: allOk,
      }]);

      if (!allOk) toast({ variant: 'destructive', title: 'Algunas acciones fallaron', description: 'Revisa el historial.' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      setHistory(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: `Error: ${msg}`,
        timestamp: new Date(),
        ok: false,
      }]);
    } finally {
      setIsExecuting(false);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [prompt, isExecuting, guildInfo, selectedGuild, toast]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Loading guilds
  if (loadingGuilds) {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Cargando servidores del bot...</p>
      </div>
    );
  }

  // Guild picker
  if (!selectedGuild) {
    return <GuildPicker guilds={guilds} onSelect={handleSelectGuild} />;
  }

  // Console
  return (
    <div className="flex flex-col gap-4" style={{ height: 'calc(100vh - 8rem)' }}>

      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl h-9 w-9"
            onClick={() => { setSelectedGuild(null); setGuildInfo(null); setHistory([]); }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <GuildAvatar guild={selectedGuild} size="sm" />
          <div>
            <h1 className="font-headline text-xl font-bold text-foreground leading-tight">{selectedGuild.name}</h1>
            <p className="text-xs text-muted-foreground">Discord Bot · Plantillas + IA</p>
          </div>
        </div>
        {loadingGuildInfo ? (
          <Badge variant="outline" className="gap-1.5">
            <Loader2 className="h-3 w-3 animate-spin" /> Cargando...
          </Badge>
        ) : guildInfo ? (
          <Badge variant="outline" className="gap-1.5 border-green-500/30 text-green-400">
            <Wifi className="h-3 w-3" />
            {guildInfo.guild.approximate_member_count?.toLocaleString('es-ES') ?? '—'} miembros
            · {guildInfo.channels.filter(c => c.type !== 4).length} canales
          </Badge>
        ) : (
          <Badge variant="outline" className="gap-1.5 border-red-500/30 text-red-400">
            <WifiOff className="h-3 w-3" /> Error
          </Badge>
        )}
      </div>

      {/* Main area */}
      <div className="flex gap-4 flex-1 min-h-0">

        {/* Channel browser */}
        <Card className="w-52 flex-shrink-0 rounded-2xl border border-white/8 bg-card/40 flex flex-col">
          <CardHeader className="pb-2 pt-4 px-4 flex-shrink-0">
            <CardTitle className="text-[0.62rem] font-bold uppercase tracking-widest text-muted-foreground/50">
              Canales
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 px-2 pb-3">
            <ScrollArea className="h-full pr-1">
              {loadingGuildInfo ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : guildInfo ? (
                <ChannelTree channels={guildInfo.channels} />
              ) : (
                <p className="text-xs text-muted-foreground/60 text-center py-8 px-2">
                  Error cargando canales.
                </p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Console (plantilla / IA) */}
        <Card className="flex-1 rounded-2xl border border-white/8 bg-card/40 flex flex-col min-w-0">
          <CardHeader className="pb-2 pt-3 px-4 flex-shrink-0">
            <div className="flex items-center gap-2 rounded-xl border border-white/8 bg-muted/30 p-1 w-fit">
              <button
                onClick={() => setMode('template')}
                className={cn('flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
                  mode === 'template' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground')}
              >
                <FileText className="h-3.5 w-3.5" /> Plantilla
              </button>
              <button
                onClick={() => setMode('ai')}
                className={cn('flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
                  mode === 'ai' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground')}
              >
                <Sparkles className="h-3.5 w-3.5" /> IA (avanzado)
              </button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col gap-3 px-4 pb-4 min-h-0">

            {mode === 'template' ? (
              <DiscordTemplateComposer
                channels={guildInfo?.channels ?? []}
                disabled={loadingGuildInfo || !guildInfo}
              />
            ) : (
            <>
            <ScrollArea className="flex-1 min-h-0">
              <div className="space-y-4 pr-2 py-1">
                {history.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground/50">
                    <Bot className="h-12 w-12 mb-3 opacity-20" />
                    <p className="text-sm">Escribe una instrucción para comenzar.</p>
                    <p className="text-xs mt-1 max-w-xs">
                      Ej: "Envía un embed dorado al canal #avisos con el título Semana Santa 2026"
                    </p>
                  </div>
                )}

                {history.map(entry => (
                  <div key={entry.id} className={cn('flex gap-2.5', entry.type === 'user' ? 'justify-end' : 'justify-start')}>
                    {entry.type === 'bot' && (
                      <div className={cn(
                        'h-7 w-7 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5',
                        entry.ok === false ? 'bg-destructive/20' : 'bg-primary/20'
                      )}>
                        <Bot className={cn('h-4 w-4', entry.ok === false ? 'text-destructive' : 'text-primary')} />
                      </div>
                    )}

                    <div className={cn(
                      'max-w-[80%] rounded-2xl px-4 py-3 text-sm',
                      entry.type === 'user'
                        ? 'bg-primary/15 border border-primary/20 rounded-tr-sm'
                        : entry.ok === false
                          ? 'bg-destructive/10 border border-destructive/20 rounded-tl-sm'
                          : 'bg-muted/40 border border-white/8 rounded-tl-sm'
                    )}>
                      <p className="leading-relaxed">{entry.content}</p>

                      {entry.results && entry.results.length > 0 && (
                        <div className="mt-2.5 space-y-1.5 border-t border-white/8 pt-2">
                          {entry.results.map((r, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              {r.error
                                ? <XCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
                                : <CheckCircle2 className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
                              }
                              <span className={cn('font-medium', r.error ? 'text-destructive/80' : 'text-muted-foreground')}>
                                {ACTION_LABELS[r.action.type as string] ?? r.action.type as string}
                              </span>
                              {r.error && <span className="text-destructive/70 truncate">{r.error}</span>}
                            </div>
                          ))}
                        </div>
                      )}

                      <p className="text-[10px] text-muted-foreground/40 mt-1.5 flex items-center gap-1.5">
                        {entry.timestamp.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                        {entry.model && (
                          <span className={cn(
                            'rounded px-1 py-0 font-medium',
                            entry.model.includes('Groq') ? 'text-amber-500/70' : 'text-primary/60'
                          )}>
                            · {entry.model}
                          </span>
                        )}
                      </p>
                    </div>

                    {entry.type === 'user' && (
                      <div className="h-7 w-7 rounded-full bg-muted flex-shrink-0 flex items-center justify-center mt-0.5">
                        <Users className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                ))}

                {isExecuting && (
                  <div className="flex gap-2.5 justify-start">
                    <div className="h-7 w-7 rounded-full bg-primary/20 flex-shrink-0 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="rounded-2xl rounded-tl-sm bg-muted/40 border border-white/8 px-4 py-3">
                      <div className="flex gap-1 items-center">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:0ms]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:150ms]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:300ms]" />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="flex gap-2 flex-shrink-0">
              <Textarea
                ref={textareaRef}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  loadingGuildInfo
                    ? 'Cargando canales...'
                    : 'Escribe una instrucción... (Enter para enviar, Shift+Enter para nueva línea)'
                }
                disabled={isExecuting || loadingGuildInfo || !guildInfo}
                className="min-h-[52px] max-h-36 resize-none rounded-xl border-white/10 bg-muted/30 text-sm focus-visible:ring-primary/50"
                rows={2}
              />
              <Button
                onClick={handleSend}
                disabled={!prompt.trim() || isExecuting || loadingGuildInfo || !guildInfo}
                size="icon"
                className="h-auto rounded-xl px-3.5 flex-shrink-0 self-end"
                style={{ height: '52px' }}
              >
                {isExecuting
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Send className="h-4 w-4" />
                }
              </Button>
            </div>
            </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
